import type {Icon} from '@primer/octicons-react'
import {KebabHorizontalIcon, SortAscIcon, SortDescIcon} from '@primer/octicons-react'
import type {ResponsiveValue} from '@primer/react'
import {ActionList, ActionMenu, IconButton, useResponsiveValue} from '@primer/react'

import styles from './SortingDropdown.module.css'

export interface OrderOptions {
  label: string
  defaultDirection: 'asc' | 'desc'
  icon: Icon
}

export interface DirectionRestrictedKeys {
  nonAscending?: string[]
  nonDescending?: string[]
}

export interface SortingDropdownProps {
  sortingItemSelected: string
  onSortingItemSelect: (key: string) => void
  defaultSortingKey: string
  sortingOptions: Record<string, OrderOptions>
  directionRestrictedKeys?: DirectionRestrictedKeys
}

const ASCENDING_SUFFIX = '-asc'

export function getSortingOptionLabel(sortingKey: string, sortingOptions: Record<string, OrderOptions>) {
  return sortingOptions[sortingKey]?.label || sortingKey
}

export function getSortingKeyAndDirection(sorting: string, defaultSortingKey: string) {
  const selectedKey = sorting || defaultSortingKey
  return splitOrderDirection(selectedKey)
}

function splitOrderDirection(value: string): [string, string] {
  if (value.endsWith(ASCENDING_SUFFIX)) {
    return [value.substring(0, value.length - ASCENDING_SUFFIX.length), 'asc']
  }

  return [value, '']
}

/**
 * SortingDropdown component for selecting sorting options.
 *
 * This component allows users to choose a sorting option from a dropdown menu.
 * It provides a visual indication of the current sorting state and allows users to change the sorting order.
 * defaultSortingKey should exist as a key on the sortingOptions.
 */
export function SortingDropdown({
  sortingItemSelected,
  onSortingItemSelect,
  defaultSortingKey,
  sortingOptions,
  directionRestrictedKeys,
}: SortingDropdownProps) {
  const [field, direction] = getSortingKeyAndDirection(sortingItemSelected, defaultSortingKey)
  const isAscending = direction === 'asc'
  const orderIcon = isAscending ? SortAscIcon : SortDescIcon
  const {nonAscending, nonDescending} = directionRestrictedKeys || {}

  function select(newField: string, newDirection: 'asc' | 'desc') {
    if (newDirection === 'asc') {
      onSortingItemSelect(`${newField}-asc`)
    } else if (newField === defaultSortingKey) {
      onSortingItemSelect('')
    } else {
      onSortingItemSelect(newField)
    }
  }

  const buttonLabel = getSortingOptionLabel(field, sortingOptions)
  const buttonAriaLabel = `Sort by ${buttonLabel} ${isAscending ? 'ascending' : 'descending'}`

  const regularAnchor = (
    <ActionMenu.Button
      leadingVisual={orderIcon}
      variant="invisible"
      aria-label={buttonAriaLabel}
      className={styles.sortDropdownButton}
    >
      {buttonLabel}
    </ActionMenu.Button>
  )

  const anchor: React.ReactElement = useResponsiveValue<ResponsiveValue<React.ReactElement>, React.ReactElement>(
    {
      narrow: (
        <ActionMenu.Anchor>
          <IconButton icon={KebabHorizontalIcon} variant="invisible" aria-label={buttonAriaLabel} />
        </ActionMenu.Anchor>
      ),
      regular: regularAnchor,
    },
    regularAnchor,
  )

  return (
    <ActionMenu>
      {anchor}
      <ActionMenu.Overlay>
        <ActionList selectionVariant="single">
          {Object.entries(sortingOptions).map(([key, {label, defaultDirection, icon: FieldIcon}]) => (
            <ActionList.Item key={key} selected={key === field} onSelect={() => select(key, defaultDirection)}>
              <ActionList.LeadingVisual>
                <FieldIcon />
              </ActionList.LeadingVisual>
              {label}
            </ActionList.Item>
          ))}
          <ActionList.Divider />
          <ActionList.Item
            disabled={nonAscending?.includes(field)}
            key="ascending"
            selected={isAscending}
            onSelect={() => select(field, 'asc')}
          >
            <ActionList.LeadingVisual>
              <SortAscIcon />
            </ActionList.LeadingVisual>
            Ascending
          </ActionList.Item>
          <ActionList.Item
            disabled={nonDescending?.includes(field)}
            key="descending"
            selected={!isAscending}
            onSelect={() => select(field, 'desc')}
          >
            <ActionList.LeadingVisual>
              <SortDescIcon />
            </ActionList.LeadingVisual>
            Descending
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
